import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Navbar from "@/components/Navbar";
import MapView from "@/components/MapView";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft,
  CarProfile,
  Phone,
  MapPin,
  Flag,
  CurrencyDollar,
  Clock,
  CheckCircle,
  XCircle,
} from "@phosphor-icons/react";

const STATUS_LABEL = {
  requested: "Finding a driver…",
  accepted: "Driver on the way",
  arrived: "Driver arrived at pickup",
  in_progress: "On the way to your destination",
  completed: "Ride completed",
  cancelled: "Ride cancelled",
  no_driver: "No driver available",
};

const STATUS_TONE = {
  requested: "bg-secondary",
  accepted: "bg-primary",
  arrived: "bg-primary",
  in_progress: "bg-accent text-accent-foreground",
  completed: "bg-secondary",
  cancelled: "bg-destructive text-destructive-foreground",
  no_driver: "bg-destructive text-destructive-foreground",
};

/**
 * Deep-link live-tracking page. Rider taps a push notification and lands
 * here — the map is centred on the driver's live marker, trail + ETA + fare
 * are shown, and the driver card exposes a tap-to-call action.
 */
export default function RideTracking() {
  const { rideId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ride, setRide] = useState(null);
  const [driverLive, setDriverLive] = useState(null); // {location, last_seen, trail, speed_kmh}
  const [eta, setEta] = useState(null); // {eta_min, distance_km}
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Fetch ride details on mount + poll status every 5s until terminal state.
  useEffect(() => {
    if (!rideId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/rides/${rideId}`);
        if (cancelled) return;
        setRide(data);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setLoading(false);
        setNotFound(e?.response?.status === 404 || e?.response?.status === 403);
      }
    };
    load();
    const id = setInterval(() => {
      if (ride && ["completed", "cancelled", "no_driver"].includes(ride.status)) return;
      load();
    }, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [rideId, ride?.status]);

  // Poll driver's live location + ETA every 4s once a driver is assigned.
  useEffect(() => {
    if (!ride || !ride.driver_id) return;
    if (["completed", "cancelled", "no_driver"].includes(ride.status)) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [locRes, etaRes] = await Promise.all([
          api.get(`/rides/${rideId}/driver-location`),
          api.get(`/rides/${rideId}/eta`).catch(() => null),
        ]);
        if (cancelled) return;
        setDriverLive(locRes.data);
        if (etaRes) setEta(etaRes.data);
      } catch { /* silent — driver may go offline briefly */ }
    };
    load();
    const id = setInterval(load, 4000);
    return () => { cancelled = true; clearInterval(id); };
  }, [rideId, ride?.driver_id, ride?.status]);

  const cancel = async () => {
    if (!confirm("Cancel this ride?")) return;
    setCancelling(true);
    try {
      await api.post(`/rides/${rideId}/cancel`);
      toast.success("Ride cancelled");
      setRide((r) => ({ ...r, status: "cancelled" }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cancel failed");
    } finally {
      setCancelling(false);
    }
  };

  // Recenter map on driver marker each tick.
  const recenter = useMemo(() => {
    if (!driverLive?.location) return null;
    return { lat: driverLive.location.lat, lng: driverLive.location.lng, ts: driverLive.last_seen };
  }, [driverLive?.location?.lat, driverLive?.location?.lng, driverLive?.last_seen]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Navbar active="ride" />
        <div className="p-8 font-mono text-sm text-foreground/60" data-testid="ride-tracking-loading">
          Loading ride…
        </div>
      </div>
    );
  }

  if (notFound || !ride) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Navbar active="ride" />
        <div className="p-8 space-y-4" data-testid="ride-tracking-not-found">
          <div className="font-mono text-xs uppercase tracking-widest text-foreground/60">Not found</div>
          <div className="text-lg">We couldn't load this ride.</div>
          <Button
            onClick={() => navigate(user?.role === "driver" ? "/driver" : "/rider")}
            className="rounded-none border-2 border-foreground"
            data-testid="ride-tracking-back-home"
          >
            <ArrowLeft size={14} className="mr-2" /> Back
          </Button>
        </div>
      </div>
    );
  }

  const label = STATUS_LABEL[ride.status] || ride.status;
  const tone = STATUS_TONE[ride.status] || "bg-secondary";
  const canCancel = ["requested", "accepted"].includes(ride.status);
  const isRider = user?.user_id === ride.rider_id;
  const drv = ride.driver || null;
  const veh = drv ? [drv.vehicle_color, drv.vehicle_make, drv.vehicle_model].filter(Boolean).join(" ") : "";
  const etaMin = eta?.eta_min;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar active="ride" />

      {/* Header */}
      <div className="border-b-2 border-foreground px-3 sm:px-6 py-3 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(user?.role === "driver" ? "/driver" : "/rider")}
          className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-wider text-foreground/70 hover:text-foreground"
          data-testid="ride-tracking-back"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div className={`ml-auto px-3 py-1 border-2 border-foreground font-mono text-[11px] uppercase tracking-wider ${tone}`} data-testid="ride-tracking-status">
          {label}
        </div>
      </div>

      {/* Map */}
      <div className="h-[45vh] sm:h-[55vh] border-b-2 border-foreground relative">
        <MapView
          pickup={ride.pickup}
          dropoff={ride.dropoff}
          drivers={
            drv && driverLive?.location
              ? [{
                  id: drv.user_id,
                  location: driverLive.location,
                  picture: drv.picture,
                  initial: (drv.name || "D")[0]?.toUpperCase(),
                  occupancy: null,
                  vehicle_color: drv.vehicle_color,
                }]
              : []
          }
          driverTrail={driverLive?.trail || []}
          recenter={recenter}
          showRoute
        />
      </div>

      {/* Info panels */}
      <div className="grid md:grid-cols-3 gap-0">
        {/* Driver card */}
        <div className="p-4 border-r-0 md:border-r-2 border-foreground md:col-span-2" data-testid="ride-tracking-driver-card">
          {drv ? (
            <div className="flex items-center gap-3">
              {drv.picture ? (
                <Link to={`/d/${drv.user_id}`}>
                  <img
                    src={drv.picture}
                    alt=""
                    className="w-14 h-14 border-2 border-foreground object-cover"
                    data-testid="ride-tracking-driver-avatar"
                  />
                </Link>
              ) : (
                <div className="w-14 h-14 border-2 border-foreground bg-foreground text-background flex items-center justify-center font-mono font-bold">
                  {(drv.name || "D")[0]?.toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <Link
                  to={`/d/${drv.user_id}`}
                  className="text-lg font-semibold hover:underline truncate block"
                  data-testid="ride-tracking-driver-name"
                >
                  {drv.name}
                </Link>
                <div className="text-sm text-foreground/70 truncate">
                  <CarProfile size={12} className="inline mr-1" /> {veh} {drv.vehicle_plate ? `· ${drv.vehicle_plate}` : ""}
                </div>
              </div>
              {isRider && drv.phone && (
                <a
                  href={`tel:${drv.phone}`}
                  className="inline-flex items-center gap-1 border-2 border-foreground bg-primary px-3 py-2 font-mono text-xs uppercase tracking-wider hover:bg-primary/80"
                  data-testid="ride-tracking-call-driver"
                >
                  <Phone size={14} weight="fill" /> Call
                </a>
              )}
            </div>
          ) : (
            <div className="text-foreground/60 font-mono text-sm" data-testid="ride-tracking-no-driver-yet">
              Waiting for a driver to accept…
            </div>
          )}
        </div>

        {/* ETA + Fare */}
        <div className="p-4 border-t-2 md:border-t-0 border-foreground bg-secondary">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">ETA</div>
              <div className="text-2xl font-bold font-mono" data-testid="ride-tracking-eta">
                {etaMin ? `${etaMin} min` : "—"}
              </div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">Fare</div>
              <div className="text-2xl font-bold font-mono" data-testid="ride-tracking-fare">
                G${ride.fare_gyd || ride.fare || 0}
              </div>
            </div>
          </div>
          <div className="mt-3 font-mono text-[10px] uppercase tracking-wider text-foreground/60 inline-flex items-center gap-1">
            <CurrencyDollar size={10} /> Cash on arrival
          </div>
        </div>
      </div>

      {/* Pickup / Dropoff */}
      <div className="p-4 border-t-2 border-foreground space-y-2 text-sm">
        <div className="flex items-start gap-2" data-testid="ride-tracking-pickup">
          <MapPin size={16} className="mt-0.5 shrink-0 text-accent" weight="fill" />
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">Pickup</div>
            <div className="truncate">{ride.pickup?.address || "—"}</div>
          </div>
        </div>
        <div className="flex items-start gap-2" data-testid="ride-tracking-dropoff">
          <Flag size={16} className="mt-0.5 shrink-0" weight="fill" />
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">Dropoff</div>
            <div className="truncate">{ride.dropoff?.address || "—"}</div>
          </div>
        </div>
      </div>

      {/* Actions */}
      {(canCancel || ride.status === "completed") && (
        <div className="p-4 border-t-2 border-foreground flex flex-wrap items-center gap-3">
          {canCancel && (
            <Button
              onClick={cancel}
              disabled={cancelling}
              className="rounded-none border-2 border-foreground bg-destructive text-destructive-foreground hover:bg-destructive/80"
              data-testid="ride-tracking-cancel"
            >
              <XCircle size={14} className="mr-2" /> Cancel ride
            </Button>
          )}
          {ride.status === "completed" && (
            <div className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider">
              <CheckCircle size={14} weight="fill" className="text-accent" />
              Completed
              <Clock size={12} className="ml-2" />
              {ride.completed_at ? new Date(ride.completed_at).toLocaleTimeString() : ""}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
